/**
 * Driver Management API Hooks
 * React hooks for managing driver data
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import * as React from 'react';
import { adminApiClient } from './adminApiClient';
import { DriverDTO } from './adminTypes';
import { ApiResponse, Pageable, PagedData } from './types';

/**
 * Hook to fetch a single driver by ID
 * @param id - Driver ID
 * @param options - Hook options
 */
export function useDriver(
  id: number,
  options: {
    enabled?: boolean;
    onSuccess?: (data: DriverDTO) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const response = await adminApiClient.get<ApiResponse<DriverDTO>>(`/drivers/${id}`);

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    return response as any;
  }, [id]);

  const query = useQuery<DriverDTO>({
    queryKey: ['driver', id],
    queryFn,
    enabled: options.enabled !== false && !!id,
  });

  // Handle callbacks using useEffect pattern (React Query v5)
  React.useEffect(() => {
    if (query.isSuccess && query.data && options.onSuccess) {
      options.onSuccess(query.data);
    }
  }, [query.isSuccess, query.data, options.onSuccess]);

  React.useEffect(() => {
    if (query.isError && query.error && options.onError) {
      options.onError(query.error);
    }
  }, [query.isError, query.error, options.onError]);

  return {
    ...query,
    data: query.data,
    loading: query.isLoading || query.isFetching,
    error: query.error,
  };
}

/**
 * Hook to fetch paginated list of drivers
 * @param params - Query parameters (page, size, sort, search, status, kycStatus, isActive)
 * @param options - Hook options
 */
export function useDrivers(
  params: {
    page?: number;
    size?: number;
    sort?: string[];
    search?: string;
    status?: string;
    kycStatus?: string;
    isActive?: boolean;
  } = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: PagedData<DriverDTO>) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Use useCallback to memoize the query function and prevent infinite re-renders
  const queryFn = useCallback(async () => {
    const queryParams: Record<string, any> = {};

    if (params.page !== undefined) {
      queryParams.page = params.page;
    }
    if (params.size !== undefined) {
      queryParams.size = params.size;
    }
    if (params.sort && params.sort.length > 0) {
      queryParams.sort = params.sort;
    }
    if (params.search) {
      queryParams.search = params.search;
    }
    if (params.status) {
      queryParams.status = params.status;
    }
    if (params.kycStatus) {
      queryParams.kycStatus = params.kycStatus;
    }
    if (params.isActive !== undefined) {
      queryParams.isActive = params.isActive;
    }

    const response = await adminApiClient.get<ApiResponse<PagedData<DriverDTO>>>(
      '/drivers/list',
      queryParams
    );

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    // If data is directly in response (array)
    if (Array.isArray(response)) {
      return {
        content: response,
        page: {
          size: response.length,
          number: 0,
          totalElements: response.length,
          totalPages: 1,
        },
        first: true,
        last: true,
        empty: response.length === 0,
      };
    }

    return response as any;
  }, [params.page, params.size, params.sort, params.search, params.status, params.kycStatus, params.isActive]);

  return useQuery<PagedData<DriverDTO>>({
    queryKey: ['drivers', params],
    queryFn,
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to create a new driver
 * @param options - Hook options
 */
export function useCreateDriver(
  options: {
    onSuccess?: (data: ApiResponse<DriverDTO>, variables: DriverDTO) => void;
    onError?: (error: Error, variables: DriverDTO) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, DriverDTO>({
    mutationFn: async (driverData: DriverDTO) => {
      const response = await adminApiClient.post<ApiResponse<DriverDTO>>('/drivers', driverData);
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to update an existing driver
 * @param options - Hook options
 */
export function useUpdateDriver(
  options: {
    onSuccess?: (
      data: ApiResponse<DriverDTO>,
      variables: { id: number; data: DriverDTO }
    ) => void;
    onError?: (error: Error, variables: { id: number; data: DriverDTO }) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, { id: number; data: DriverDTO }>({
    mutationFn: async ({ id, data }: { id: number; data: DriverDTO }) => {
      const response = await adminApiClient.put<ApiResponse<DriverDTO>>(`/drivers/${id}`, data);
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to delete a driver
 * @param options - Hook options
 */
export function useDeleteDriver(
  options: {
    onSuccess?: (data: ApiResponse<void>, variables: DeleteDriverVariables) => void;
    onError?: (error: Error, variables: DeleteDriverVariables) => void;
  } = {}
) {
  return useMutation<ApiResponse<void>, Error, DeleteDriverVariables>({
    mutationFn: async (variables: DeleteDriverVariables) => {
      // First delete the driver
      const response = await adminApiClient.delete<ApiResponse<void>>(`/drivers/${variables.driverId}`);

      // Then find and delete the corresponding user if email is provided
      if (variables.email) {
        try {
          const userResponse = await adminApiClient.get<any>(`/api/admin/user-by-email?email=${encodeURIComponent(variables.email)}`);
          if (userResponse.data?.id) {
            await adminApiClient.delete<ApiResponse<void>>(`/api/admin/${userResponse.data.id}`);
          }
        } catch (userError) {
          // Log the error but don't fail the entire operation
          console.warn('Failed to delete corresponding user:', userError);
        }
      }

      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Variables for delete driver operation
 */
export interface DeleteDriverVariables {
  driverId: number;
  email?: string;
  phoneNumber?: string;
}

/**
 * Hook to fetch drivers by KYC status
 * @param kycStatus - KYC status filter
 * @param options - Hook options
 */
export function useDriversByKycStatus(
  kycStatus?: string,
  options: {
    enabled?: boolean;
    onSuccess?: (data: DriverDTO[]) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryFn = useCallback(async () => {
    const queryParams: Record<string, any> = {};
    if (kycStatus) {
      queryParams.kycStatus = kycStatus;
    }

    const response = await adminApiClient.get<ApiResponse<DriverDTO[]>>(
      '/drivers/drivers-list-by-kyc-status',
      queryParams
    );

    if (response.data) {
      return response.data;
    }

    return response as any;
  }, [kycStatus]);

  return useQuery<DriverDTO[]>({
    queryKey: ['drivers-by-kyc-status', kycStatus],
    queryFn,
    enabled: options.enabled !== false,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to toggle driver active status
 * @param options - Hook options
 */
export function useToggleDriverActiveStatus(
  options: {
    onSuccess?: (data: ApiResponse<DriverDTO>, variables: number) => void;
    onError?: (error: Error, variables: number) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, number>({
    mutationFn: async (id: number) => {
      const response = await adminApiClient.get<ApiResponse<DriverDTO>>(
        `/drivers/toggle-active-status/${id}`
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to upload profile photo
 * @param options - Hook options
 */
export function useUploadProfilePhoto(
  options: {
    onSuccess?: (data: ApiResponse<DriverDTO>, variables: { driverId: number; file: File }) => void;
    onError?: (error: Error, variables: { driverId: number; file: File }) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, { driverId: number; file: File }>({
    mutationFn: async ({ driverId, file }) => {
      const formData = new FormData();
      formData.append('profilePhotoUrl', file);

      const response = await adminApiClient.put<ApiResponse<DriverDTO>>(
        `/drivers/upload-profile-photo/${driverId}`,
        formData
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to upload KYC documents
 * @param options - Hook options
 */
export function useUploadKycDocuments(
  options: {
    onSuccess?: (
      data: ApiResponse<DriverDTO>,
      variables: {
        driverId: number;
        govtIdType: string;
        govtIdNumber: string;
        files: {
          govtIdFrontUrl: File;
          govtIdBackUrl: File;
          drivingLicenseFrontUrl: File;
          drivingLicenseBackUrl: File;
          selfieWithIdUrl: File;
        };
      }
    ) => void;
    onError?: (
      error: Error,
      variables: {
        driverId: number;
        govtIdType: string;
        govtIdNumber: string;
        files: {
          govtIdFrontUrl: File;
          govtIdBackUrl: File;
          drivingLicenseFrontUrl: File;
          drivingLicenseBackUrl: File;
          selfieWithIdUrl: File;
        };
      }
    ) => void;
  } = {}
) {
  return useMutation<
    ApiResponse<DriverDTO>,
    Error,
    {
      driverId: number;
      govtIdType: string;
      govtIdNumber: string;
      files: {
        govtIdFrontUrl: File;
        govtIdBackUrl: File;
        drivingLicenseFrontUrl: File;
        drivingLicenseBackUrl: File;
        selfieWithIdUrl: File;
      };
    }
  >({
    mutationFn: async ({ driverId, govtIdType, govtIdNumber, files }) => {
      const formData = new FormData();
      formData.append('govtIdFrontUrl', files.govtIdFrontUrl);
      formData.append('govtIdBackUrl', files.govtIdBackUrl);
      formData.append('drivingLicenseFrontUrl', files.drivingLicenseFrontUrl);
      formData.append('drivingLicenseBackUrl', files.drivingLicenseBackUrl);
      formData.append('selfieWithIdUrl', files.selfieWithIdUrl);

      const queryParams = {
        govtIdType,
        govtIdNumber,
        driverId,
      };

      const response = await adminApiClient.post<ApiResponse<DriverDTO>>(
        '/drivers/upload-kyc-documents',
        formData,
        { params: queryParams }
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to upload vehicle details (POST - combines details + documents)
 * @param options - Hook options
 */
export function useUploadVehicleDetails(
  options: {
    onSuccess?: (
      data: ApiResponse<DriverDTO>,
      variables: {
        driverId: number;
        vehicleType: string;
        vehicleCategoryId: number;
        vehicleMake: string;
        vehicleModel: string;
        vehicleRegistrationNumber: string;
        yearOfManufacture: number;
        vehicleColor: string;
        seatingCapacity: number;
        luggageCapacity: number;
        files: {
          ownershipDocUrl: File;
          insuranceDocUrl: File;
          roadTaxDocUrl: File;
        };
      }
    ) => void;
    onError?: (error: Error, variables: any) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, any>({
    mutationFn: async ({
      driverId,
      vehicleType,
      vehicleCategoryId,
      vehicleMake,
      vehicleModel,
      vehicleRegistrationNumber,
      yearOfManufacture,
      vehicleColor,
      seatingCapacity,
      luggageCapacity,
      files,
    }) => {
      const formData = new FormData();
      formData.append('ownershipDocUrl', files.ownershipDocUrl);
      formData.append('insuranceDocUrl', files.insuranceDocUrl);
      formData.append('roadTaxDocUrl', files.roadTaxDocUrl);

      const queryParams = {
        driverId,
        vehicleType,
        vehicleCategoryId,
        vehicleMake,
        vehicleModel,
        vehicleRegistrationNumber,
        yearOfManufacture,
        vehicleColor,
        seatingCapacity,
        luggageCapacity,
      };

      const response = await adminApiClient.post<ApiResponse<DriverDTO>>(
        '/drivers/upload-vehicle-details',
        formData,
        { params: queryParams }
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to update vehicle details (PUT - details only, no documents)
 * @param options - Hook options
 */
export function useUpdateVehicleDetails(
  options: {
    onSuccess?: (
      data: ApiResponse<DriverDTO>,
      variables: {
        driverId: number;
        vehicleType: string;
        vehicleCategoryId: number;
        vehicleMake: string;
        vehicleModel: string;
        vehicleRegistrationNumber: string;
        yearOfManufacture: number;
        vehicleColor: string;
        seatingCapacity: number;
        luggageCapacity: number;
      }
    ) => void;
    onError?: (error: Error, variables: any) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, any>({
    mutationFn: async ({
      driverId,
      vehicleType,
      vehicleCategoryId,
      vehicleMake,
      vehicleModel,
      vehicleRegistrationNumber,
      yearOfManufacture,
      vehicleColor,
      seatingCapacity,
      luggageCapacity,
    }) => {
      const queryParams = {
        vehicleType,
        vehicleCategoryId,
        vehicleMake,
        vehicleModel,
        vehicleRegistrationNumber,
        yearOfManufacture,
        vehicleColor,
        seatingCapacity,
        luggageCapacity,
      };

      const response = await adminApiClient.put<ApiResponse<DriverDTO>>(
        `/drivers/update-vehicle-details/${driverId}`,
        null,
        { params: queryParams }
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to update vehicle documents
 * @param options - Hook options
 */
export function useUpdateVehicleDocuments(
  options: {
    onSuccess?: (
      data: ApiResponse<DriverDTO>,
      variables: {
        driverId: number;
        files: {
          ownershipDocUrl: File;
          insuranceDocUrl: File;
          roadTaxDocUrl: File;
        };
      }
    ) => void;
    onError?: (error: Error, variables: any) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, any>({
    mutationFn: async ({ driverId, files }) => {
      const formData = new FormData();
      formData.append('ownershipDocUrl', files.ownershipDocUrl);
      formData.append('insuranceDocUrl', files.insuranceDocUrl);
      formData.append('roadTaxDocUrl', files.roadTaxDocUrl);

      const response = await adminApiClient.put<ApiResponse<DriverDTO>>(
        `/drivers/update-vehicle-documents/${driverId}`,
        formData
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to upload banking and payout info (POST)
 * @param options - Hook options
 */
export function useUploadBankingInfo(
  options: {
    onSuccess?: (
      data: ApiResponse<DriverDTO>,
      variables: {
        driverId: number;
        bankAccountHolderName: string;
        bankName: string;
        accountNumber: string;
        ifscCode: string;
        swiftCode: string;
        eWalletId: string;
        paymentFrequency: string;
      }
    ) => void;
    onError?: (error: Error, variables: any) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, any>({
    mutationFn: async ({
      driverId,
      bankAccountHolderName,
      bankName,
      accountNumber,
      ifscCode,
      swiftCode,
      eWalletId,
      paymentFrequency,
    }) => {
      const queryParams = {
        driverId,
        bankAccountHolderName,
        bankName,
        accountNumber,
        ifscCode,
        swiftCode,
        eWalletId,
        paymentFrequency,
      };

      const response = await adminApiClient.post<ApiResponse<DriverDTO>>(
        '/drivers/banking-and-payout-info',
        null,
        { params: queryParams }
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to update banking and payout info (PUT)
 * @param options - Hook options
 */
export function useUpdateBankingInfo(
  options: {
    onSuccess?: (
      data: ApiResponse<DriverDTO>,
      variables: {
        driverId: number;
        bankAccountHolderName: string;
        bankName: string;
        accountNumber: string;
        ifscCode: string;
        swiftCode: string;
        eWalletId: string;
        paymentFrequency: string;
      }
    ) => void;
    onError?: (error: Error, variables: any) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, any>({
    mutationFn: async ({
      driverId,
      bankAccountHolderName,
      bankName,
      accountNumber,
      ifscCode,
      swiftCode,
      eWalletId,
      paymentFrequency,
    }) => {
      const queryParams = {
        bankAccountHolderName,
        bankName,
        accountNumber,
        ifscCode,
        swiftCode,
        eWalletId,
        paymentFrequency,
      };

      const response = await adminApiClient.put<ApiResponse<DriverDTO>>(
        `/drivers/update-banking-and-payout-info/${driverId}`,
        null,
        { params: queryParams }
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to update personal details
 * @param options - Hook options
 */
export function useUpdatePersonalDetails(
  options: {
    onSuccess?: (
      data: ApiResponse<DriverDTO>,
      variables: { id: number; data: DriverDTO }
    ) => void;
    onError?: (error: Error, variables: { id: number; data: DriverDTO }) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, { id: number; data: DriverDTO }>({
    mutationFn: async ({ id, data }) => {
      const response = await adminApiClient.put<ApiResponse<DriverDTO>>(
        `/drivers/update-personal-details/${id}`,
        data
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to approve KYC documents
 * @param options - Hook options
 */
export function useApproveKyc(
  options: {
    onSuccess?: (data: ApiResponse<DriverDTO>, variables: number) => void;
    onError?: (error: Error, variables: number) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, number>({
    mutationFn: async (driverId: number) => {
      const response = await adminApiClient.put<ApiResponse<DriverDTO>>(
        `/drivers/approve-kyc-documents/${driverId}`
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to reject KYC documents
 * @param options - Hook options
 */
export function useRejectKyc(
  options: {
    onSuccess?: (
      data: ApiResponse<DriverDTO>,
      variables: { driverId: number; rejectionReason: string }
    ) => void;
    onError?: (
      error: Error,
      variables: { driverId: number; rejectionReason: string }
    ) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, { driverId: number; rejectionReason: string }>({
    mutationFn: async ({ driverId, rejectionReason }) => {
      const response = await adminApiClient.put<ApiResponse<DriverDTO>>(
        `/drivers/reject-kyc-documents/${driverId}`,
        null,
        { params: { rejectionReason } }
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to keep KYC documents in view
 * @param options - Hook options
 */
export function useKeepInViewKyc(
  options: {
    onSuccess?: (data: ApiResponse<DriverDTO>, variables: number) => void;
    onError?: (error: Error, variables: number) => void;
  } = {}
) {
  return useMutation<ApiResponse<DriverDTO>, Error, number>({
    mutationFn: async (driverId: number) => {
      const response = await adminApiClient.put<ApiResponse<DriverDTO>>(
        `/drivers/keepinview-kyc-documents/${driverId}`
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to download driver document
 * @param options - Hook options
 */
export function useDownloadDriverDocument(
  options: {
    onSuccess?: (data: Blob, variables: string) => void;
    onError?: (error: Error, variables: string) => void;
  } = {}
) {
  return useMutation<Blob, Error, string>({
    mutationFn: async (filePath: string) => {
      const response = await adminApiClient.get<Blob>(
        '/drivers/driver-document-download',
        { filePath },
        { responseType: 'blob' }
      );
      return response;
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}
